import { jsPDF, defaultAutoTableConfig } from './pdfConfig';
import { 
  safePdfText, 
  createEnhancedTitlePage, 
  addModernSectionHeader,
  addEnhancedPageHeader,
  addEnhancedPageFooter,
  addInfoBox,
  addStatusIndicator 
} from './pdfConfigEnhanced';
import { EXTENDED_MATERIALS } from './ExtendedMaterialDatabase';
import { convertPolishChars } from '../../utils/pdfFonts';

// Użyj EXTENDED_MATERIALS lub fallback do pustego obiektu
const MATERIALS = EXTENDED_MATERIALS || {};

// Typy dla danych raportu
export interface ReportData {
  input: {
    material: string;
    thickness: number;
    width: number;
    height: number;
    load: number;
    loadType: 'uniform' | 'point';
    supportType: string;
    environmentType: string;
    safetyFactor: number;
  };
  result: {
    maxDeflection: number;
    maxStress: number;
    safetyMargin: number;
    passed: boolean;
    criticalPoints: Array<{
      x: number;
      y: number;
      stress: number;
      deflection: number;
    }>;
    recommendations: string[];
  };
  optimalSolutions?: Array<{
    material: string;
    thickness: number;
    maxDeflection: number;
    maxStress: number;
    safetyMargin: number;
    weight: number;
    totalCost: number;
    lifeExpectancy: number;
    confidenceLevel: number;
    needsSupport: boolean;
    needsReinforcement: boolean;
    supportConfig?: {
      type: string;
      material: string;
      cost: number;
      installationNotes?: string[];
    };
    reinforcementConfig?: {
      type: string;
      pattern: string;
      manufacturingMethod: string;
    };
  }>;
  visualization?: {
    stressMap: any[];
    deflectionMap: any[];
  };
  date: Date;
  projectName?: string;
  clientName?: string;
  notes?: string;
}

/**
 * Generator raportów PDF dla kalkulatora wytrzymałości
 */
class StrengthReportGenerator {
  private doc: jsPDF;
  private currentY: number;
  private pageHeight: number;
  private pageWidth: number;
  private margin: number;
  private primaryColor: [number, number, number];
  private secondaryColor: [number, number, number];
  
  constructor() {
    // Constructor only sets initial values
    // Font registration happens in generateReport (async)
    this.currentY = 25;
    this.pageHeight = 297;
    this.pageWidth = 210;
    this.margin = 12;
    this.primaryColor = [33, 150, 243]; // Niebieski
    this.secondaryColor = [66, 66, 66]; // Ciemnoszary
  }
  
  /**
   * Generuj raport PDF
   */
  private drawPlateSchematic(
    width: number,
    height: number,
    thickness: number,
    supportType: string,
    loadType: string,
    load: number,
    y: number
  ): number {
    const schematicWidth = 170;
    const schematicHeight = 100;
    const startX = 20;
    
    // Tło
    this.doc.setFillColor(250, 250, 250);
    this.doc.rect(startX, y, schematicWidth, schematicHeight, 'F');
    
    // Perspektywa 3D płyty
    const plateX = startX + 30;
    const plateY = y + 30;
    const plateW = 80;
    const plateH = 50;
    const offset = 15; // Przesunięcie dla efektu 3D
    
    // Tylna ściana
    this.doc.setFillColor(200, 200, 200);
    this.doc.setDrawColor(100, 100, 100);
    this.doc.rect(plateX + offset, plateY - offset, plateW, plateH, 'FD');
    
    // Górna powierzchnia
    this.doc.setFillColor(230, 230, 230);
    const topPoints = [
      [plateX, plateY],
      [plateX + plateW, plateY],
      [plateX + plateW + offset, plateY - offset],
      [plateX + offset, plateY - offset]
    ];
    this.doc.lines([
      [plateW, 0],
      [offset, -offset],
      [-plateW, 0],
      [-offset, offset]
    ], plateX, plateY, [1, 1], 'FD');
    
    // Przednia ściana
    this.doc.setFillColor(255, 255, 255);
    this.doc.rect(plateX, plateY, plateW, plateH, 'FD');
    
    // Boczna ściana
    this.doc.setFillColor(220, 220, 220);
    this.doc.lines([
      [offset, -offset],
      [0, plateH],
      [-offset, offset],
      [0, -plateH]
    ], plateX + plateW, plateY, [1, 1], 'FD');
    
    // Wymiary
    this.doc.setFontSize(9);
    this.doc.setTextColor(0, 0, 0);
    
    // Szerokość
    this.doc.setDrawColor(0, 0, 0);
    this.doc.setLineWidth(0.3);
    this.doc.line(plateX, plateY + plateH + 5, plateX + plateW, plateY + plateH + 5);
    this.doc.text(`${width} mm`, plateX + plateW/2, plateY + plateH + 10, { align: 'center' });
    
    // Wysokość
    this.doc.line(plateX - 5, plateY, plateX - 5, plateY + plateH);
    this.doc.text(`${height} mm`, plateX - 10, plateY + plateH/2, { align: 'right' });
    
    // Grubość
    this.doc.text(`Grubość: ${thickness} mm`, plateX + plateW + offset + 10, plateY);
    
    // Podpory
    this.doc.setFillColor(100, 100, 100);
    if (supportType === 'four_sides') {
      // Podpory na wszystkich bokach
      this.doc.rect(plateX - 3, plateY - 3, plateW + 6, 3, 'F');
      this.doc.rect(plateX - 3, plateY + plateH, plateW + 6, 3, 'F');
      this.doc.rect(plateX - 3, plateY, 3, plateH, 'F');
      this.doc.rect(plateX + plateW, plateY, 3, plateH, 'F');
      this.doc.text('Podparta na 4 bokach', plateX + plateW/2, plateY - 10, { align: 'center' });
    } else if (supportType === 'two_sides') {
      // Podpory na dwóch bokach
      this.doc.rect(plateX - 3, plateY, 3, plateH, 'F');
      this.doc.rect(plateX + plateW, plateY, 3, plateH, 'F');
      this.doc.text('Podparta na 2 bokach', plateX + plateW/2, plateY - 10, { align: 'center' });
    }
    
    // Obciążenie
    this.doc.setDrawColor(255, 0, 0);
    this.doc.setLineWidth(1);
    if (loadType === 'uniform') {
      // Strzałki równomierne
      for (let x = plateX + 10; x < plateX + plateW; x += 15) {
        for (let y2 = plateY + 10; y2 < plateY + plateH; y2 += 15) {
          this.drawArrow(x, y2 - 20, x, y2 - 5);
        }
      }
      this.doc.setTextColor(255, 0, 0);
      this.doc.text(`${load} kg/m²`, plateX + plateW/2, plateY - 25, { align: 'center' });
    } else if (loadType === 'point') {
      // Pojedyncza strzałka
      const centerX = plateX + plateW/2;
      const centerY = plateY + plateH/2;
      this.drawArrow(centerX, centerY - 25, centerX, centerY - 5);
      this.doc.setTextColor(255, 0, 0);
      this.doc.text(`${load} kg`, centerX, centerY - 30, { align: 'center' });
    }
    
    return schematicHeight + 10;
  }
  
  private drawArrow(x1: number, y1: number, x2: number, y2: number): void {
    const headLength = 3;
    const angle = Math.atan2(y2 - y1, x2 - x1);
    
    // Ustaw kolor czerwony dla strzałek
    this.doc.setDrawColor(255, 0, 0);
    this.doc.setLineWidth(0.5);
    
    // Linia główna
    this.doc.line(x1, y1, x2, y2);
    
    // Grot strzałki
    this.doc.line(
      x2,
      y2,
      x2 - headLength * Math.cos(angle - Math.PI / 6),
      y2 - headLength * Math.sin(angle - Math.PI / 6)
    );
    this.doc.line(
      x2,
      y2,
      x2 - headLength * Math.cos(angle + Math.PI / 6),
      y2 - headLength * Math.sin(angle + Math.PI / 6)
    );
    
    // Przywróć domyślny kolor
    this.doc.setDrawColor(0, 0, 0);
  }
  
  public generateReport(data: ReportData): void {
    // Create new document
    this.doc = new jsPDF();
    this.currentY = 25;
    
    this.debugLog('Starting report generation');
    
    // Strona tytułowa
    this.addTitlePage(data);
    
    // Podsumowanie wyników
    this.addSummarySection(data);
    
    // Szczegółowa analiza
    this.addDetailedAnalysisSection(data);
    
    // Wizualizacje
    if (data.visualization) {
      this.addVisualizationSection(data);
    }
    
    // Rekomendacje
    this.addRecommendationsSection(data);
    
    // Optymalne rozwiązania
    if (data.optimalSolutions && data.optimalSolutions.length > 0) {
      this.addOptimalSolutionsSection(data);
    }
    
    // Dane techniczne materiału
    this.addMaterialDataSection(data);
    
    // Stopki na wszystkich stronach
    this.addFooters(data);
    
    // Zapisz PDF
    const fileName = `raport_wytrzymalosci_${data.projectName || 'projekt'}_${data.date.toISOString().split('T')[0]}.pdf`;
    this.doc.save(convertPolishChars(fileName));
  }
  
  /**
   * Strona tytulowa
   */
  private addTitlePage(data: ReportData) {
    // Use enhanced title page
    createEnhancedTitlePage(
      this.doc,
      'RAPORT ANALIZY WYTRZYMALOSCI',
      'Analiza konstrukcji z tworzyw sztucznych',
      data.projectName,
      data.clientName
    );
    
    // Informacje o projekcie - dostosowane do nowego układu
    this.currentY = 120;  // Zmniejszone ze 150 na 120
    this.doc.setTextColor(...this.secondaryColor);
    this.doc.setFontSize(11);  // Zmniejszone z 12
    
    const projectInfo = [
      ['Nazwa projektu:', safePdfText(data.projectName || 'Bez nazwy')],
      ['Klient:', safePdfText(data.clientName || 'Nie okreslono')],
      ['Data analizy:', data.date.toLocaleDateString('pl-PL')],
      ['Godzina:', data.date.toLocaleTimeString('pl-PL')],
      ['Material:', safePdfText(MATERIALS[data.input.material].displayName)],
      ['Wymiary:', `${data.input.width} x ${data.input.height} x ${data.input.thickness} mm`],
      ['Obciazenie:', `${data.input.load} kg${data.input.loadType === 'uniform' ? '/m2' : ''}`]
    ];
    
    projectInfo.forEach(([label, value]) => {
      this.doc.setFont(undefined, 'bold');
      this.doc.text(label, this.margin, this.currentY);
      this.doc.setFont(undefined, 'normal');
      this.doc.text(convertPolishChars(value), 75, this.currentY);  // Zmniejszone z 80
      this.currentY += 6;  // Zmniejszone z 7
    });
    
    // Dodaj schemat 3D konstrukcji na pierwszej stronie
    this.currentY += 10;  // Zmniejszone z 15
    this.doc.setTextColor(...this.secondaryColor);
    this.doc.setFontSize(13);  // Zmniejszone z 14
    this.doc.text('SCHEMAT KONSTRUKCJI:', this.margin, this.currentY);
    this.currentY += 8;  // Zmniejszone z 10
    
    const schematicHeight = this.drawPlateSchematic(
      data.input.width,
      data.input.height,
      data.input.thickness,
      data.input.supportType,
      data.input.loadType,
      data.input.load,
      this.currentY
    );
    this.currentY += schematicHeight;
    
    // Status wyniku - duży
    this.currentY += 20;
    const status = data.result.passed ? 'KONSTRUKCJA SPEŁNIA WYMAGANIA' : 'KONSTRUKCJA NIE SPEŁNIA WYMAGAŃ';
    const statusColor = data.result.passed ? [0, 200, 0] : [255, 0, 0];
    
    this.doc.setFillColor(...(statusColor as [number, number, number]));
    this.doc.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, 30, 'F');
    
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(18);
    this.doc.text(status, this.pageWidth / 2, this.currentY + 18, { align: 'center' });
    
    // Kluczowe parametry
    this.currentY += 50;
    this.doc.setTextColor(...this.secondaryColor);
    this.doc.setFontSize(14);
    this.doc.text('KLUCZOWE PARAMETRY:', this.margin, this.currentY);
    
    this.currentY += 10;
    const keyParams = [
      {
        label: 'Maksymalne ugięcie:',
        value: `${data.result.maxDeflection.toFixed(2)} mm`,
        limit: `(limit: ${(data.input.height / 200).toFixed(2)} mm)`,
        ok: data.result.maxDeflection <= data.input.height / 200
      },
      {
        label: 'Maksymalne naprężenie:',
        value: `${data.result.maxStress.toFixed(1)} MPa`,
        limit: `(limit: ${(MATERIALS[data.input.material].flexuralStrength / data.input.safetyFactor).toFixed(1)} MPa)`,
        ok: data.result.maxStress <= MATERIALS[data.input.material].flexuralStrength / data.input.safetyFactor
      },
      {
        label: 'Margines bezpieczeństwa:',
        value: `${data.result.safetyMargin.toFixed(0)}%`,
        limit: '(minimum: 0%)',
        ok: data.result.safetyMargin >= 0
      }
    ];
    
    keyParams.forEach(param => {
      // Ikona statusu
      const iconColor = param.ok ? [0, 200, 0] : [255, 0, 0];
      this.doc.setFillColor(...(iconColor as [number, number, number]));
      this.doc.circle(this.margin + 5, this.currentY - 2, 3, 'F');
      
      this.doc.setTextColor(255, 255, 255);
      this.doc.setFontSize(10);
      this.doc.text(param.ok ? '✓' : '✗', this.margin + 5, this.currentY, { align: 'center' });
      
      // Tekst
      this.doc.setTextColor(...this.secondaryColor);
      this.doc.setFontSize(11);
      this.doc.setFont(undefined, 'bold');
      this.doc.text(param.label, this.margin + 15, this.currentY);
      
      this.doc.setFont(undefined, 'normal');
      this.doc.text(param.value, 90, this.currentY);
      
      this.doc.setTextColor(150, 150, 150);
      this.doc.setFontSize(9);
      this.doc.text(param.limit, 130, this.currentY);
      
      this.currentY += 8;
    });
  }
  
  /**
   * Sekcja podsumowania
   */
  private addSummarySection(data: ReportData) {
    this.doc.addPage();
    this.currentY = this.margin;
    
    this.addSectionHeader('PODSUMOWANIE ANALIZY');
    
    // Tabela wyników
    const summaryData = [
      ['Parametr', 'Wartość obliczona', 'Wartość dopuszczalna', 'Status'],
      [
        'Ugięcie maksymalne',
        `${data.result.maxDeflection.toFixed(2)} mm`,
        `${(data.input.height / 200).toFixed(2)} mm`,
        this.getStatusIcon(data.result.maxDeflection <= data.input.height / 200)
      ],
      [
        'Naprężenie maksymalne',
        `${data.result.maxStress.toFixed(1)} MPa`,
        `${(MATERIALS[data.input.material].flexuralStrength / data.input.safetyFactor).toFixed(1)} MPa`,
        this.getStatusIcon(data.result.maxStress <= MATERIALS[data.input.material].flexuralStrength / data.input.safetyFactor)
      ],
      [
        'Współczynnik wykorzystania',
        `${((data.result.maxStress / MATERIALS[data.input.material].flexuralStrength) * 100).toFixed(0)}%`,
        '100%',
        this.getStatusIcon(data.result.maxStress <= MATERIALS[data.input.material].flexuralStrength)
      ],
      [
        'Margines bezpieczeństwa',
        `${data.result.safetyMargin.toFixed(0)}%`,
        '> 0%',
        this.getStatusIcon(data.result.safetyMargin > 0)
      ]
    ];
    
    this.doc.autoTable({
      startY: this.currentY,
      head: [summaryData[0]],
      body: summaryData.slice(1),
      theme: 'grid',
      headStyles: {
        fillColor: this.primaryColor,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9
      },
      styles: {
        fontSize: 9,
        cellPadding: 3
      },
      columnStyles: {
        0: { cellWidth: 'auto', fontStyle: 'bold' },
        1: { cellWidth: 'auto', halign: 'right' },
        2: { cellWidth: 'auto', halign: 'right' },
        3: { cellWidth: 25, halign: 'center' }
      },
      margin: { left: 15, right: 15 }
    });
    
    this.currentY = this.doc.lastAutoTable.finalY + 10;
  }
  
  /**
   * Sekcja szczegółowej analizy
   */
  private addDetailedAnalysisSection(data: ReportData) {
    this.addSectionHeader('SZCZEGÓŁOWA ANALIZA');
    
    // Parametry wejściowe
    this.doc.setFontSize(11);
    this.doc.setTextColor(...this.secondaryColor);
    this.doc.text('Parametry konstrukcji:', this.margin, this.currentY);
    
    this.currentY += 5;
    
    const inputData = [
      ['Materiał', MATERIALS[data.input.material].displayName],
      ['Wymiary płyty', `${data.input.width} × ${data.input.height} mm`],
      ['Grubość', `${data.input.thickness} mm`],
      ['Typ obciążenia', data.input.loadType === 'uniform' ? 'Równomierne' : 'Punktowe'],
      ['Wartość obciążenia', `${data.input.load} kg${data.input.loadType === 'uniform' ? '/m²' : ''}`],
      ['Typ podparcia', this.translateSupportType(data.input.supportType)],
      ['Środowisko pracy', data.input.environmentType],
      ['Współczynnik bezpieczeństwa', data.input.safetyFactor.toString()]
    ];
    
    this.doc.autoTable({
      startY: this.currentY,
      body: inputData,
      theme: 'plain',
      styles: {
        fontSize: 9,
        cellPadding: 2
      },
      columnStyles: {
        0: { cellWidth: 'auto', fontStyle: 'bold', minCellWidth: 40 },
        1: { cellWidth: 'auto' }
      },
      margin: { left: 15, right: 15 }
    });
    
    this.currentY = this.doc.lastAutoTable.finalY + 10;
    
    // Punkty krytyczne
    if (data.result.criticalPoints && data.result.criticalPoints.length > 0) {
      this.doc.text('Punkty krytyczne konstrukcji:', this.margin, this.currentY);
      this.currentY += 5;
      
      const criticalPointsData = data.result.criticalPoints.map((point, index) => [
        `Punkt ${index + 1}`,
        `(${point.x.toFixed(0)}, ${point.y.toFixed(0)})`,
        `${point.stress.toFixed(1)} MPa`,
        `${point.deflection.toFixed(2)} mm`
      ]);
      
      this.doc.autoTable({
        startY: this.currentY,
        head: [['Punkt', 'Współrzędne [mm]', 'Naprężenie', 'Ugięcie']],
        body: criticalPointsData,
        theme: 'striped',
        headStyles: {
          fillColor: this.secondaryColor,
          textColor: [255, 255, 255]
        },
        styles: {
          fontSize: 9
        }
      });
      
      this.currentY = this.doc.lastAutoTable.finalY + 10;
    }
  }
  
  /**
   * Sekcja wizualizacji
   */
  private addVisualizationSection(data: ReportData) {
    this.doc.addPage();
    this.currentY = this.margin;
    
    this.addSectionHeader('WIZUALIZACJA WYNIKÓW');
    
    // Schemat konstrukcji
    this.doc.setFontSize(10);
    this.doc.setTextColor(...this.secondaryColor);
    this.doc.text('Schemat obciążenia konstrukcji:', this.margin, this.currentY);
    
    this.currentY += 10;
    
    // Rysuj schemat
    this.drawStructureSchema(data);
    
    this.currentY += 60;
    
    // Mapa naprężeń
    if (data.visualization?.stressMap) {
      this.doc.text('Rozkład naprężeń:', this.margin, this.currentY);
      this.currentY += 10;
      
      // Tu można dodać wizualizację mapy naprężeń
      this.drawStressMap(data.visualization.stressMap);
      
      this.currentY += 50;
    }
    
    // Mapa ugięć
    if (data.visualization?.deflectionMap) {
      this.doc.text('Rozkład ugięć:', this.margin, this.currentY);
      this.currentY += 10;
      
      // Tu można dodać wizualizację mapy ugięć
      this.drawDeflectionMap(data.visualization.deflectionMap);
      
      this.currentY += 50;
    }
    
    // Legenda
    this.drawLegend();
  }
  
  /**
   * Rysuj schemat konstrukcji
   */
  private drawStructureSchema(data: ReportData) {
    const centerX = this.pageWidth / 2;
    const centerY = this.currentY + 20;
    const scale = 0.3;
    const width = data.input.width * scale;
    const height = 10; // Grubość na rysunku
    
    // Płyta
    this.doc.setFillColor(200, 200, 200);
    this.doc.rect(centerX - width / 2, centerY - height / 2, width, height, 'F');
    
    // Podpory
    this.drawSupports(centerX - width / 2, centerY + height / 2, width, data.input.supportType);
    
    // Obciążenie
    this.drawLoad(centerX - width / 2, centerY - height / 2, width, data.input.loadType, data.input.load);
    
    // Wymiary
    this.drawDimension(
      centerX - width / 2,
      centerY + 20,
      centerX + width / 2,
      centerY + 20,
      `${data.input.width} mm`
    );
    
    // Grubość
    this.doc.setFontSize(8);
    this.doc.setTextColor(...this.secondaryColor);
    this.doc.text(`t = ${data.input.thickness} mm`, centerX + width / 2 + 5, centerY);
  }
  
  /**
   * Rysuj podpory
   */
  private drawSupports(x: number, y: number, width: number, supportType: string) {
    this.doc.setFillColor(100, 100, 100);
    
    switch (supportType) {
      case 'simply_supported':
        // Rysuj trójkąty ręcznie jako wielokąty
        // Lewy trójkąt
        this.doc.lines([
          [-3, 5],
          [6, 0],
          [-3, -5]
        ], x, y + 3, [1, 1], 'F');
        
        // Prawy trójkąt
        this.doc.lines([
          [-3, 5],
          [6, 0],
          [-3, -5]
        ], x + width, y + 3, [1, 1], 'F');
        break;
        
      case 'cantilever':
        // Utwierdzone z lewej
        this.doc.rect(x - 5, y - 10, 5, 20, 'F');
        break;
        
      case 'four_sides':
        // Linia ciągła pod spodem
        this.doc.setLineWidth(2);
        this.doc.line(x - 5, y + 5, x + width + 5, y + 5);
        break;
    }
  }
  
  /**
   * Rysuj obciążenie
   */
  private drawLoad(x: number, y: number, width: number, loadType: string, load: number) {
    this.doc.setDrawColor(255, 102, 0);
    this.doc.setLineWidth(1);
    
    if (loadType === 'uniform') {
      // Strzałki równomiernie rozłożone
      const arrows = 5;
      for (let i = 0; i <= arrows; i++) {
        const arrowX = x + (i / arrows) * width;
        this.drawArrowWithSize(arrowX, y - 10, arrowX, y, 2);
      }
      
      // Etykieta
      this.doc.setFontSize(8);
      this.doc.text(`${load} kg/m²`, x + width / 2, y - 15, { align: 'center' });
      
    } else if (loadType === 'point') {
      // Pojedyncza strzałka w środku
      const centerX = x + width / 2;
      this.drawArrowWithSize(centerX, y - 15, centerX, y, 3);
      
      // Etykieta
      this.doc.setFontSize(8);
      this.doc.text(`${load} kg`, centerX, y - 20, { align: 'center' });
    }
  }
  
  /**
   * Rysuj strzałkę (dla drawLoad)
   */
  private drawArrowWithSize(x1: number, y1: number, x2: number, y2: number, size: number = 3) {
    this.doc.line(x1, y1, x2, y2);
    
    // Grot strzałki
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const arrowAngle = Math.PI / 6;
    
    this.doc.line(
      x2,
      y2,
      x2 - size * Math.cos(angle - arrowAngle),
      y2 - size * Math.sin(angle - arrowAngle)
    );
    
    this.doc.line(
      x2,
      y2,
      x2 - size * Math.cos(angle + arrowAngle),
      y2 - size * Math.sin(angle + arrowAngle)
    );
  }
  
  /**
   * Rysuj wymiar
   */
  private drawDimension(x1: number, y1: number, x2: number, y2: number, text: string) {
    this.doc.setLineWidth(0.2);
    this.doc.setDrawColor(0, 0, 0);
    
    // Linie pomocnicze
    this.doc.line(x1, y1 - 2, x1, y1 + 2);
    this.doc.line(x2, y2 - 2, x2, y2 + 2);
    
    // Linia wymiarowa
    this.doc.line(x1, y1, x2, y2);
    
    // Tekst
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;
    
    // Tło dla tekstu
    this.doc.setFillColor(255, 255, 255);
    this.doc.rect(midX - 10, midY - 3, 20, 6, 'F');
    
    this.doc.setFontSize(7);
    this.doc.text(text, midX, midY + 1, { align: 'center' });
  }
  
  /**
   * Rysuj mapę naprężeń (uproszczona)
   */
  private drawStressMap(stressMap: any[]) {
    const x = this.margin;
    const y = this.currentY;
    const width = this.pageWidth - 2 * this.margin;
    const height = 40;
    
    // Gradient naprężeń
    const steps = 10;
    for (let i = 0; i < steps; i++) {
      const ratio = i / steps;
      const red = Math.floor(255 * ratio);
      const green = Math.floor(255 * (1 - ratio));
      
      this.doc.setFillColor(red, green, 0);
      this.doc.rect(x + (width / steps) * i, y, width / steps, height, 'F');
    }
    
    // Ramka
    this.doc.setDrawColor(0, 0, 0);
    this.doc.rect(x, y, width, height, 'S');
    
    // Etykiety
    this.doc.setFontSize(8);
    this.doc.setTextColor(...this.secondaryColor);
    this.doc.text('0 MPa', x, y + height + 5);
    this.doc.text('Max', x + width - 10, y + height + 5);
  }
  
  /**
   * Rysuj mapę ugięć (uproszczona)
   */
  private drawDeflectionMap(deflectionMap: any[]) {
    const x = this.margin;
    const y = this.currentY;
    const width = this.pageWidth - 2 * this.margin;
    const height = 40;
    
    // Gradient ugięć
    const steps = 10;
    for (let i = 0; i < steps; i++) {
      const ratio = i / steps;
      const blue = Math.floor(255 * (1 - ratio));
      
      this.doc.setFillColor(0, 0, blue);
      this.doc.rect(x + (width / steps) * i, y, width / steps, height, 'F');
    }
    
    // Ramka
    this.doc.setDrawColor(0, 0, 0);
    this.doc.rect(x, y, width, height, 'S');
    
    // Etykiety
    this.doc.setFontSize(8);
    this.doc.setTextColor(...this.secondaryColor);
    this.doc.text('0 mm', x, y + height + 5);
    this.doc.text('Max', x + width - 10, y + height + 5);
  }
  
  /**
   * Rysuj legendę
   */
  private drawLegend() {
    this.doc.setFontSize(10);
    this.doc.setTextColor(...this.secondaryColor);
    this.doc.text('Legenda:', 30, this.currentY);
    
    this.currentY += 5;
    
    const legendItems = [
      { color: [255, 0, 0], label: 'Wysokie naprężenia (>80% wytrzymałości)' },
      { color: [255, 255, 0], label: 'Średnie naprężenia (50-80% wytrzymałości)' },
      { color: [0, 255, 0], label: 'Niskie naprężenia (<50% wytrzymałości)' },
      { color: [100, 100, 100], label: 'Podpory / mocowania' }
    ];
    
    legendItems.forEach(item => {
      // Kolorowy prostokąt
      this.doc.setFillColor(...(item.color as [number, number, number]));
      this.doc.rect(35, this.currentY - 3, 5, 4, 'F');
      
      // Tekst
      this.doc.setFontSize(8);
      this.doc.setTextColor(...this.secondaryColor);
      this.doc.text(item.label, 45, this.currentY);
      
      this.currentY += 5;
    });
  }
  
  /**
   * Sekcja rekomendacji
   */
  private addRecommendationsSection(data: ReportData) {
    this.addSectionHeader('REKOMENDACJE I ZALECENIA');
    
    data.result.recommendations.forEach((rec, index) => {
      // Ikona
      const icon = rec.startsWith('✅') ? '✓' : rec.startsWith('⚠️') ? '!' : rec.startsWith('❌') ? '✗' : '•';
      const color = rec.startsWith('✅') ? [0, 200, 0] : rec.startsWith('⚠️') ? [255, 165, 0] : rec.startsWith('❌') ? [255, 0, 0] : this.secondaryColor;
      
      // Ramka dla rekomendacji
      this.doc.setDrawColor(200, 200, 200);
      this.doc.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, 15, 'S');
      
      // Ikona w kółku
      this.doc.setFillColor(...(color as [number, number, number]));
      this.doc.circle(this.margin + 7, this.currentY + 7.5, 4, 'F');
      this.doc.setTextColor(255, 255, 255);
      this.doc.setFontSize(10);
      this.doc.text(icon, this.margin + 7, this.currentY + 9, { align: 'center' });
      
      // Treść
      this.doc.setTextColor(...this.secondaryColor);
      this.doc.setFontSize(9);
      const cleanText = rec.replace(/[✅⚠️❌💡]/g, '').trim();
      const lines = this.doc.splitTextToSize(cleanText, this.pageWidth - 2 * this.margin - 20);
      this.doc.text(lines, this.margin + 15, this.currentY + 6);
      
      this.currentY += 18;
      
      // Nowa strona jeśli potrzeba
      if (this.currentY > this.pageHeight - 40) {
        this.doc.addPage();
        this.currentY = this.margin;
      }
    });
  }
  
  /**
   * Sekcja optymalnych rozwiązań
   */
  private addOptimalSolutionsSection(data: ReportData) {
    this.addSectionHeader('OPTYMALNE ROZWIĄZANIA KONSTRUKCYJNE');
    
    data.optimalSolutions?.forEach((solution, index) => {
      // Nagłówek rozwiązania
      this.doc.setFillColor(...this.primaryColor);
      this.doc.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, 8, 'F');
      this.doc.setTextColor(255, 255, 255);
      this.doc.setFontSize(10);
      this.doc.text(
        `Rozwiązanie ${index + 1}: ${MATERIALS[solution.material].displayName} ${solution.thickness}mm`,
        this.margin + 5,
        this.currentY + 5
      );
      
      this.currentY += 10;
      
      // Parametry rozwiązania
      const solutionData = [
        ['Materiał', MATERIALS[solution.material].displayName],
        ['Grubość', `${solution.thickness} mm`],
        ['Ugięcie maksymalne', `${solution.maxDeflection.toFixed(2)} mm`],
        ['Naprężenie maksymalne', `${solution.maxStress.toFixed(1)} MPa`],
        ['Margines bezpieczeństwa', `${solution.safetyMargin.toFixed(0)}%`],
        ['Waga konstrukcji', `${solution.weight.toFixed(1)} kg`],
        ['Koszt całkowity', `${solution.totalCost.toFixed(0)} PLN`],
        ['Żywotność', `${solution.lifeExpectancy} lat`],
        ['Poziom pewności', `${solution.confidenceLevel}%`]
      ];
      
      // Dodaj info o wsparciu
      if (solution.needsSupport && solution.supportConfig) {
        solutionData.push(
          ['Wymagane wsparcie', solution.supportConfig.type],
          ['Materiał wsparcia', solution.supportConfig.material],
          ['Koszt wsparcia', `${solution.supportConfig.cost.toFixed(0)} PLN`]
        );
      }
      
      // Dodaj info o wzmocnieniu
      if (solution.needsReinforcement && solution.reinforcementConfig) {
        solutionData.push(
          ['Wzmocnienie', solution.reinforcementConfig.type],
          ['Wzór wzmocnienia', solution.reinforcementConfig.pattern],
          ['Metoda produkcji', solution.reinforcementConfig.manufacturingMethod]
        );
      }
      
      this.doc.autoTable({
        startY: this.currentY,
        body: solutionData,
        theme: 'plain',
        styles: {
          fontSize: 9,
          cellPadding: 2
        },
        columnStyles: {
          0: { cellWidth: 'auto', fontStyle: 'bold', minCellWidth: 40 },
          1: { cellWidth: 'auto' }
        },
        margin: { left: 15, right: 15 },
        tableWidth: 'auto'
      });
      
      this.currentY = this.doc.lastAutoTable.finalY + 5;
      
      // Uwagi montażowe
      if (solution.supportConfig?.installationNotes) {
        this.doc.setFontSize(9);
        this.doc.setTextColor(...this.secondaryColor);
        this.doc.text('Uwagi montażowe:', this.margin, this.currentY);
        this.currentY += 4;
        
        solution.supportConfig.installationNotes.forEach(note => {
          this.doc.text(`• ${note}`, this.margin + 5, this.currentY);
          this.currentY += 4;
        });
      }
      
      this.currentY += 10;
      
      // Nowa strona jeśli potrzeba
      if (this.currentY > this.pageHeight - 60) {
        this.doc.addPage();
        this.currentY = this.margin;
      }
    });
  }
  
  /**
   * Sekcja danych technicznych materiału
   */
  private addMaterialDataSection(data: ReportData) {
    this.doc.addPage();
    this.currentY = this.margin;
    
    this.addSectionHeader('DANE TECHNICZNE MATERIAŁU');
    
    const material = MATERIALS[data.input.material];
    
    const materialData = [
      ['Nazwa handlowa', material.displayName],
      ['Gęstość', `${material.density} kg/m³`],
      ['Moduł Younga', `${material.modulusOfElasticity} MPa`],
      ['Wytrzymałość na rozciąganie', `${material.tensileStrength} MPa`],
      ['Wytrzymałość na zginanie', `${material.flexuralStrength} MPa`],
      ['Wytrzymałość na ściskanie', `${material.compressiveStrength} MPa`],
      ['Wytrzymałość na ścinanie', `${material.shearStrength} MPa`],
      ['Współczynnik Poissona', material.poissonsRatio.toString()],
      ['Max temperatura pracy', `${material.maxServiceTemp}°C`],
      ['Rozszerzalność termiczna', `${material.thermalExpansion} × 10⁻⁶/K`],
      ['Odporność na uderzenia', `${material.impactStrength} kJ/m²`],
      ['Odporność UV', material.uvResistance ? 'TAK' : 'NIE'],
      ['Odporność chemiczna', material.chemicalResistance.toUpperCase()],
      ['Orientacyjna cena', `${material.pricePerKg} PLN/kg`]
    ];
    
    this.doc.autoTable({
      startY: this.currentY,
      head: [['Parametr', safePdfText('Wartosc')]],
      body: materialData,
      theme: 'grid',
      headStyles: {
        fillColor: this.primaryColor,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9
      },
      styles: {
        fontSize: 9,
        cellPadding: 3
      },
      columnStyles: {
        0: { cellWidth: 'auto', fontStyle: 'bold', minCellWidth: 50 },
        1: { cellWidth: 'auto' }
      },
      margin: { left: 15, right: 15 },
      tableWidth: 'auto'
    });
    
    this.currentY = this.doc.lastAutoTable.finalY + 10;
    
    // Uwagi dodatkowe
    if (data.notes) {
      this.addSectionHeader('UWAGI DODATKOWE');
      
      this.doc.setFontSize(10);
      this.doc.setTextColor(...this.secondaryColor);
      const lines = this.doc.splitTextToSize(data.notes, this.pageWidth - 2 * this.margin);
      this.doc.text(lines, this.margin, this.currentY);
    }
  }
  
  /**
   * Dodaj stopki do wszystkich stron
   */
  private addFooters(data: ReportData) {
    const pageCount = this.doc.getNumberOfPages();
    
    for (let i = 1; i <= pageCount; i++) {
      this.doc.setPage(i);
      
      // Linia
      this.doc.setDrawColor(200, 200, 200);
      this.doc.line(this.margin, this.pageHeight - 15, this.pageWidth - this.margin, this.pageHeight - 15);
      
      // Tekst stopki
      this.doc.setFontSize(8);
      this.doc.setTextColor(150, 150, 150);
      
      // Lewa strona
      this.doc.text(
        'PlexiSystem - Kalkulator Wytrzymałości v2.0',
        this.margin,
        this.pageHeight - 10
      );
      
      // Środek
      this.doc.text(
        `Raport wygenerowany: ${data.date.toLocaleString('pl-PL')}`,
        this.pageWidth / 2,
        this.pageHeight - 10,
        { align: 'center' }
      );
      
      // Prawa strona
      this.doc.text(
        `Strona ${i} z ${pageCount}`,
        this.pageWidth - this.margin,
        this.pageHeight - 10,
        { align: 'right' }
      );
    }
  }
  
  /**
   * Dodaj nagłówek sekcji
   */
  private addSectionHeader(title: string) {
    // Sprawdź czy jest miejsce
    if (this.currentY > this.pageHeight - 50) {
      this.doc.addPage();
      this.currentY = this.margin;
    }
    
    this.doc.setFillColor(...this.primaryColor);
    this.doc.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, 8, 'F');
    
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(12);
    this.doc.text(title, this.margin + 5, this.currentY + 5.5);
    
    this.currentY += 12;
  }
  
  /**
   * Pomocnicze funkcje
   */
  private translateSupportType(type: string): string {
    const translations: Record<string, string> = {
      'simply_supported': 'Podparte na dwóch końcach',
      'cantilever': 'Wspornik',
      'fixed_both': 'Utwierdzone na obu końcach',
      'three_sides': 'Podparte na 3 bokach',
      'four_sides': 'Podparte na 4 bokach',
      'point_supports': 'Podpory punktowe'
    };
    return translations[type] || type;
  }
  
  private getStatusIcon(isOk: boolean): string {
    return isOk ? '✓' : '✗';
  }
  
  /**
   * Debug logging
   */
  private debugLog(section: string): void {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      console.log(`[PDF] ${section}`);
      console.log(`[PDF] CurrentY: ${this.currentY}, Page: ${this.doc.getNumberOfPages()}`);
    }
  }
  
  /**
   * Check if new page is needed
   */
  private checkNewPage(requiredSpace: number = 30): void {
    const bottomMargin = 25;
    
    if (this.currentY + requiredSpace > this.pageHeight - bottomMargin) {
      this.doc.addPage();
      this.addPageHeader();
      this.addPageFooter(this.doc.getNumberOfPages());
      this.currentY = 25;
      
      this.debugLog('New page added');
    }
  }
  
  /**
   * Add page header
   */
  private addPageHeader(): void {
    // Logo background
    this.doc.setFillColor(255, 165, 0);
    this.doc.rect(0, 0, this.pageWidth, 15, 'F');
    
    // Header text
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(12);
    this.doc.setTextColor(255, 255, 255);
    this.doc.text('PlexiSystem', 12, 10);
    
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9);
    this.doc.text('Raport Analizy Wytrzymalosci', this.pageWidth - 12, 10, { align: 'right' });
    
    // Line under header
    this.doc.setDrawColor(200, 200, 200);
    this.doc.line(12, 18, this.pageWidth - 12, 18);
    
    this.currentY = 25;
  }
  
  /**
   * Add page footer
   */
  private addPageFooter(pageNumber: number): void {
    const footerY = this.pageHeight - 15;
    
    // Line above footer
    this.doc.setDrawColor(200, 200, 200);
    this.doc.line(12, footerY - 3, this.pageWidth - 12, footerY - 3);
    
    // Footer text
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(8);
    this.doc.setTextColor(120, 120, 120);
    
    // Left - date
    this.doc.text(
      `Wygenerowano: ${new Date().toLocaleDateString('pl-PL')}`,
      12,
      footerY
    );
    
    // Center - page number
    this.doc.text(
      `Strona ${pageNumber}`,
      this.pageWidth / 2,
      footerY,
      { align: 'center' }
    );
    
    // Right - website
    this.doc.text(
      'www.plexisystem.pl',
      this.pageWidth - 12,
      footerY,
      { align: 'right' }
    );
  }
}

// Eksportuj instancję generatora
export const strengthReportGenerator = new StrengthReportGenerator();